import {CollectionShare} from '@blesk-frontend/api';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Loader,
} from '@blesk-frontend/ui';
import {ClipboardCheck, Link2} from 'lucide-react';
import {MouseEvent, useState} from 'react';

import {useTimeout} from '@/hooks';
import {copyToClipboard} from '@/libs';
import {sendEvent} from '@/libs/analytics';

interface CollectionShareModalProps {
  share: CollectionShare;
  onUpdate: (type: CollectionShare['type']) => Promise<void>;
}

export const CollectionShareMenu = ({share, onUpdate}: CollectionShareModalProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {isWaiting: isLinkCopied, restart, clear} = useTimeout(2000);

  const onClickUpdate = async (e: MouseEvent, type: CollectionShare['type']) => {
    e.preventDefault();

    setLoading(true);
    clear();
    await onUpdate(type);
    setLoading(false);
  };

  const onClickCopyLink = async (e: MouseEvent) => {
    e.preventDefault();

    if (share?.type === 'link') {
      await copyToClipboard(`${import.meta.env.VITE_GALLERY_URL}/collection/${share.link}`);
      restart();
    }
    sendEvent('collections/share-copy-link');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">Поделиться</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="mt-2 flex flex-col rounded-md bg-bg-secondary text-text-secondary shadow-md"
      >
        <DropdownMenuGroup>
          <DropdownMenuCheckboxItem
            className="cursor-pointer justify-between gap-3 pr-8"
            checked={share?.type === 'private'}
            onClick={(e) => onClickUpdate(e, 'private')}
          >
            <span>Доступно только вам</span>
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            className="cursor-pointer justify-between gap-3 pr-8"
            checked={share?.type === 'link'}
            onClick={(e) => onClickUpdate(e, 'link')}
          >
            <span>Всем у кого есть ссылка</span>
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={cn(
            'flex cursor-pointer items-center gap-2 text-paragraph-sm',
            isLinkCopied && 'focus:bg-success-subtle',
          )}
          disabled={loading || share.type === 'private'}
          onClick={onClickCopyLink}
        >
          {!isLinkCopied ? (
            <>
              {loading ? <Loader size={14} /> : <Link2 className="size-3.5" />}
              <span>Скопировать ссылку</span>
            </>
          ) : (
            <>
              <ClipboardCheck className="size-3.5 text-success-primary" />
              <span className="text-success-primary">Ссылка скопирована</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
